import { useMemo } from 'react'
import {
  useGetPlayerIdView,
  useGetPlayerIdSplitStats,
  useGetPlayerIdHistory,
  useGetPlayerIdStats,
  useGetPlayerIdAchievementsAwards,
} from '@/api/generated/player-player/player-player'
import type { PlayerCardData } from './types'
import { mapPlayerView } from './mappers/view'
import { mapSplits, mapSituational } from './mappers/splits'
import { mapHistory } from './mappers/history'
import { mapAchievements } from './mappers/achievements'
import { mapStats, mapExtendedBatting, mapExtendedPitching, mapFielding } from './mappers/stats'

export interface CardFlags { summary?: boolean; history?: boolean; fullStats?: boolean }
export interface UsePlayerCardResult { data: PlayerCardData | undefined; isLoading: boolean; isError: boolean }

export function usePlayerCard(id: number | null | undefined, flags: CardFlags = {}): UsePlayerCardResult {
  const valid = id != null
  const view = useGetPlayerIdView(id as number, { query: { enabled: valid } })
  const wantSplits = valid && Boolean(flags.summary || flags.fullStats)
  const wantHistory = valid && Boolean(flags.summary || flags.history)
  const wantFullStats = valid && Boolean(flags.fullStats)
  // Regular stats (season-by-season) back the Summary snapshot's expand-to-all-
  // seasons as well as the Full Stats tab, so load them for either. Same query
  // key, so TanStack Query shares the result across tabs.
  const wantRegularStats = valid && Boolean(flags.summary || flags.fullStats)
  const splitStats = useGetPlayerIdSplitStats(id as number, { query: { enabled: wantSplits } })
  const history = useGetPlayerIdHistory(id as number, { query: { enabled: wantHistory } })
  const achievements = useGetPlayerIdAchievementsAwards(id as number, { query: { enabled: wantHistory } })
  const statsRegular = useGetPlayerIdStats(id as number, { part: 'regular' }, { query: { enabled: wantRegularStats } })
  const statsPlayoffs = useGetPlayerIdStats(id as number, { part: 'playoffs' }, { query: { enabled: wantFullStats } })

  const data = useMemo<PlayerCardData | undefined>(() => {
    if (!view.data) return undefined
    const core = mapPlayerView(view.data)
    if (splitStats.data) {
      const s = mapSplits(splitStats.data); core.splitStats = s.current; core.splitStatsHistory = s.history; core.splitStatsCareer = s.career
      const t = mapSituational(splitStats.data); core.situationalStats = t.current; core.situationalStatsHistory = t.history; core.situationalStatsCareer = t.career
    }
    if (history.data) { const h = mapHistory(history.data); core.ratingsHistory = h.ratingsHistory; core.transactions = h.transactions }
    if (achievements.data) { const a = mapAchievements(achievements.data); core.achievements = a.achievements; core.awards = a.awards }
    if (statsRegular.data) {
      core.seasonStats = mapStats(statsRegular.data).seasonStats
      core.extendedBattingStats = mapExtendedBatting(statsRegular.data)
      core.extendedPitchingStats = mapExtendedPitching(statsRegular.data)
      core.fieldingStats = mapFielding(statsRegular.data)
    }
    if (statsPlayoffs.data) { core.playoffStats = mapStats(statsPlayoffs.data).seasonStats }
    return core
  }, [view.data, splitStats.data, history.data, achievements.data, statsRegular.data, statsPlayoffs.data])

  return { data, isLoading: view.isLoading, isError: view.isError }
}
